/**
 * Validation of right-panel state read back from storage.
 *
 * Deliberately *not* a port of t3code's `migratePersistedRightPanelState`. That
 * function is eleven schema versions of their history — surface kinds they added
 * and removed, id schemes they re-keyed — none of which ever happened here.
 * Copying it would import a decade of someone else's migrations as dead
 * branches nothing can reach.
 *
 * What Forge needs instead is the durable part: persisted state is untrusted
 * input. It was written by an older build, possibly hand-edited, and the shape
 * that comes back may not be the shape we defined. Anything unrecognised is
 * dropped rather than repaired — a surface we cannot render is worse than a
 * missing tab, because it wedges the panel on a kind with no renderer.
 */
import {
  EnvironmentId,
  ProjectId,
  TurnId,
  type SourceControlProviderKind,
} from "@cmmd-forge/contracts";

import {
  RIGHT_PANEL_KINDS,
  pullRequestSurface,
  type RightPanelKind,
  type RightPanelSurface,
  type ThreadRightPanelState,
} from "./rightPanelSurface";

type Unknown = Record<string, unknown>;
const PULL_REQUESTS_PANEL_KEY = "workspace:pull-requests";

function isRecord(value: unknown): value is Unknown {
  return typeof value === "object" && value !== null;
}

function isKind(value: unknown): value is RightPanelKind {
  return typeof value === "string" && (RIGHT_PANEL_KINDS as readonly string[]).includes(value);
}

function readPositiveInt(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(1, Math.trunc(value))
    : null;
}

function readRequestId(value: unknown): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

/**
 * Ids are re-derived from the payload rather than taken as written: a stored
 * `file:` id that disagrees with its own `relativePath` would make every lookup
 * by id miss the surface that was just opened.
 */
function readFileSurface(value: Unknown): RightPanelSurface | null {
  const relativePath = value["relativePath"];
  if (typeof relativePath !== "string" || relativePath.length === 0) return null;
  return {
    id: `file:${relativePath}`,
    kind: "file",
    relativePath,
    revealLine: readPositiveInt(value["revealLine"]),
    revealRequestId: readRequestId(value["revealRequestId"]),
    readPolicy: value["readPolicy"] === "connected-only" ? "connected-only" : "execution-intent",
  };
}

function readPreviewSurface(value: Unknown): RightPanelSurface | null {
  const resourceId = value["resourceId"];
  if (resourceId === null || resourceId === undefined) {
    return { id: "browser:new", kind: "preview", resourceId: null };
  }
  if (typeof resourceId !== "string" || resourceId.length === 0) return null;
  return { id: `browser:${resourceId}`, kind: "preview", resourceId };
}

function readDiffSurface(value: Unknown): RightPanelSurface {
  const turnId = value["turnId"];
  const filePath = value["filePath"];
  // `TurnId.make` is how the rest of the app brands a turn id read from
  // untrusted input; a bare string cast would let any string through.
  const readTurnId = typeof turnId === "string" && turnId.length > 0 ? TurnId.make(turnId) : null;
  return {
    id: "diff",
    kind: "diff",
    turnId: readTurnId,
    // A file selection with no turn addresses nothing, so it is dropped rather
    // than restored as a dangling reference.
    filePath: readTurnId && typeof filePath === "string" && filePath.length > 0 ? filePath : null,
  };
}

const SOURCE_CONTROL_PROVIDERS: ReadonlySet<string> = new Set([
  "github",
  "gitlab",
  "azure-devops",
  "bitbucket",
  "unknown",
]);

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isOptionalNullableString(value: unknown): value is string | null | undefined {
  return value === undefined || isNullableString(value);
}

/**
 * Reads a persisted pull-request surface, or null when any required field
 * fails validation.
 */
function readPullRequestSurface(value: Unknown): RightPanelSurface | null {
  const provider = value["provider"];
  const environmentId = value["environmentId"];
  const repositoryLinkId = value["repositoryLinkId"];
  const projectId = value["projectId"];
  const host = value["host"];
  const repository = value["repository"];
  const number = readPositiveInt(value["number"]);
  const title = value["title"];
  const valid = [
    isNonEmptyString(provider) && SOURCE_CONTROL_PROVIDERS.has(provider),
    isNonEmptyString(environmentId),
    isNullableString(repositoryLinkId),
    isNonEmptyString(projectId),
    isNonEmptyString(host),
    isNonEmptyString(repository),
    number !== null,
    isOptionalNullableString(title),
  ].every(Boolean);
  if (!valid || !isNonEmptyString(provider) || !isNonEmptyString(environmentId)) {
    return null;
  }
  return pullRequestSurface({
    provider: provider as SourceControlProviderKind,
    environmentId: EnvironmentId.make(environmentId),
    repositoryLinkId: repositoryLinkId as string | null,
    projectId: ProjectId.make(projectId as string),
    host: host as string,
    repository: repository as string,
    number: number as number,
    ...(typeof title === "string" || title === null ? { title } : {}),
  });
}

/**
 * A renamed tab keeps its name across reloads; a blank or non-string one
 * falls back to the derived label rather than storing a rename that nothing
 * would display.
 */
function readLabel(value: Unknown): string | undefined {
  const label = value["label"];
  if (typeof label !== "string") return undefined;
  const trimmed = label.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** One surface, or `null` if it cannot be trusted. */
function readSurface(value: unknown): RightPanelSurface | null {
  if (!isRecord(value) || !isKind(value["kind"])) return null;
  const kind = value["kind"];
  const surface =
    kind === "file"
      ? readFileSurface(value)
      : kind === "preview"
        ? readPreviewSurface(value)
        : kind === "diff"
          ? readDiffSurface(value)
          : kind === "pull-request"
            ? readPullRequestSurface(value)
            : { id: kind, kind };
  if (surface === null) return null;
  const label = readLabel(value);
  return label === undefined ? surface : { ...surface, label };
}

/** Deduplicated by id — two tabs sharing one id would close each other. */
function readSurfaceList(value: unknown): RightPanelSurface[] {
  const surfaces: RightPanelSurface[] = [];
  const seen = new Set<string>();
  for (const entry of Array.isArray(value) ? value : []) {
    const surface = readSurface(entry);
    if (!surface || seen.has(surface.id)) continue;
    seen.add(surface.id);
    surfaces.push(surface);
  }
  return surfaces;
}

function readThreadState(value: unknown): ThreadRightPanelState | null {
  if (!isRecord(value)) return null;

  const surfaces = readSurfaceList(value["surfaces"]);
  if (surfaces.length === 0) return null;

  const rawActive = value["activeSurfaceId"];
  const hasActive = surfaces.some((surface) => surface.id === rawActive);
  const activeSurfaceId =
    typeof rawActive === "string" && hasActive ? rawActive : (surfaces[0]?.id ?? null);

  return {
    // A panel restored open with nothing in it is an empty shell the user has
    // to close by hand, so `isOpen` is only honoured when something survived.
    isOpen: value["isOpen"] === true,
    activeSurfaceId,
    surfaces,
  };
}

export function migratePersistedRightPanelState(persisted: unknown): {
  byThreadKey: Record<string, ThreadRightPanelState>;
} {
  if (!isRecord(persisted) || !isRecord(persisted["byThreadKey"])) {
    return { byThreadKey: {} };
  }

  const byThreadKey: Record<string, ThreadRightPanelState> = {};
  for (const [threadKey, rawState] of Object.entries(persisted["byThreadKey"])) {
    if (threadKey === PULL_REQUESTS_PANEL_KEY) continue;
    const threadState = readThreadState(rawState);
    if (threadState) byThreadKey[threadKey] = threadState;
  }
  return { byThreadKey };
}
